import React, { useState } from 'react';
import { ChevronDown, Plus, MoreVertical } from 'lucide-react';
// import LockIcon from '../../assets/icons/lockIcon.svg';
import DropdownMenu from './DropdownMenu';
import Tooltip from './Tooltip';
import { InfoTooltip } from './InfoTooltip';

/* ------------------------- Generic Sidebar Item ------------------------- */
const SidebarItem = ({
	id,
	name,
	activeSection,
	handleNavigate,
	openDropdown,
	toggleDropdown,
	isLastItem,
	canDelete,
	onDelete,
	onRename,
	onMove,
	onPreview,
	onSampleContent
}) => {
	// If text is longer than 25 characters, place tooltip on left, otherwise on top
	const tooltipPlacement = name && name.length > 25 ? 'left' : 'top';

	return (
		<div
			role="button"
			tabIndex={0}
			onMouseDown={(e) => e.preventDefault()}
			onClick={() => id && handleNavigate(id)}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					id && handleNavigate(id);
				}
			}}
			className={`flex items-center justify-between w-full text-base text-left transition-colors relative cursor-pointer ${activeSection === id ? 'text-purple bg-purple/10' : 'text-gray hover:text-dark-blue'
				}`}
		>
			<div className="flex items-center gap-2 text-base min-w-0 flex-1 text-left">
				{/* <img src={LockIcon} alt="Lock" className="w-3 h-3 shrink-0" /> */}
				<span className="truncate block text-left">{name}</span>
				<Tooltip placement={tooltipPlacement} content="Subsection">
					<div className="shrink-0">
						<InfoTooltip />
					</div>
				</Tooltip>
			</div>

			{activeSection === id && (
				<div className="relative shrink-0 ml-2">
					<MoreVertical
						className="w-4 h-4 text-gray cursor-pointer"
						onClick={(e) => {
							e.stopPropagation();
							toggleDropdown(id);
						}}
					/>
					<DropdownMenu
						itemId={id}
						isOpen={openDropdown === id}
						onClose={() => toggleDropdown(null)}
						isLastItem={isLastItem}
						canDelete={canDelete}
						onDelete={() => onDelete(id)}
						onRename={() => onRename(id)}
						onMove={(itemId, direction) => onMove(itemId, direction)}
						onPreview={() => onPreview(id)}
						onSampleContent={() => onSampleContent(id)}
					/>
				</div>
			)}
		</div>
	);
};

/* -------------------- Optional Section Item (Add Sections area) -------------------- */
const OptionalSectionItem = ({ id, name, onAdd }) => {
	return (
		<div
			className="flex items-center justify-between w-full text-base text-left py-1 px-2 rounded transition-colors text-gray hover:text-dark-blue hover:bg-gray-50"
		>
			<div className="flex items-center gap-2 text-base min-w-0 flex-1 text-left">
				<span className="truncate block text-left opacity-70">{name}</span>
			</div>
			<button
				onClick={(e) => {
					e.stopPropagation();
					onAdd(id);
				}}
				className="shrink-0 ml-2 p-1 rounded hover:bg-purple/10 transition-colors"
				title={`Add "${name}" to document`}
			>
				<Plus className="w-4 h-4 text-purple" />
			</button>
		</div>
	);
};

/* --------------------- Main EditorSidebarSection ---------------------- */
export default function EditorSidebarSection({
	sections,
	onNavigate,
	onMoveSection,      // (categoryId, sectionId, direction) => void
	onMoveSubsection,   // (categoryId, sectionId, subsectionId, direction) => void
	onAddSection,       // (categoryId, sectionId) => void
	onDeleteSection,    // (categoryId, sectionId) => void - for deleting added sections
	onPreviewSection,   // (sectionId) => void - for previewing section/subsection
	onSampleContent     // (sectionId) => void - for viewing sample content
}) {
	const [openSections, setOpenSections] = useState({});
	const [activeSection, setActiveSection] = useState(null);
	const [openDropdown, setOpenDropdown] = useState(null);

	const toggleSection = (key) => {
		setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
		if (openSections[key]) {
			setOpenDropdown(null);
			setActiveSection(null);
		}
	};

	const handleNavigate = (id) => {
		setActiveSection(id);
		onNavigate(id);
	};

	const toggleDropdown = (id) => {
		setOpenDropdown(openDropdown === id ? null : id);
	};

	// Check if using new category-based structure
	const isCategories =
		sections.length > 0 && sections[0].name !== undefined && sections[0].sections !== undefined;

	return (
		<div className="w-full bg-white sticky top-0 h-screen overflow-y-auto ">
			{isCategories
				? // New category-based structure
				sections.map((category, categoryIdx) => {
					const isOpen = openSections[category.name];

					return (
						<div
							key={category.id || categoryIdx}
							className={`flex flex-col w-full border-b border-lightGrey px-[25px] ${isOpen ? 'gap-[30px] py-[18px]' : 'py-[18px]'
								}`}
						>
							{/* Category Header */}
							<button
								onClick={() => toggleSection(category.name)}
								className="w-full flex justify-between items-center text-dark-blue font-semibold text-20 text-left"
							>
								<div className="flex items-center gap-2 text-left min-w-0 flex-1">
									<span className="truncate w-full block">{category.name}</span>
								</div>
								<div
									className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'
										}`}
								>
									<ChevronDown className="w-4 h-4" />
								</div>
							</button>

							{/* Expanded Content - Sections and Subsections */}
							{isOpen && (
								<div className="flex flex-col items-start gap-2 w-full max-w-full ">
									{category.sections?.map((section, sectionIdx) => (
										<div key={section.id || sectionIdx} className="w-full">
											{/* Section Item */}
											<SidebarItem
												id={section.id}
												name={section.title}
												activeSection={activeSection}
												handleNavigate={handleNavigate}
												openDropdown={openDropdown}
												toggleDropdown={toggleDropdown}
												isLastItem={
													sectionIdx === category.sections.length - 1 &&
													!section.subsections?.length
												}
												canDelete={section.canDelete}
												onDelete={(id) => {
													if (section.canDelete && onDeleteSection) {
														onDeleteSection(category.id, id);
													}
												}}
												onRename={(id) => console.log('Rename Section', id)}
												onMove={(itemId, direction) => {
													// Move section within its category
													if (onMoveSection) {
														onMoveSection(category.id, itemId, direction);
													}
												}}
												onPreview={(id) => {
													if (onPreviewSection) onPreviewSection(id);
												}}
												onSampleContent={(id) => {
													if (onSampleContent) onSampleContent(id);
												}}
											/>

											{/* Subsections */}
											{section.subsections && section.subsections.length > 0 && (
												<div className="ml-4 mt-2 flex flex-col gap-2">
													{section.subsections.map((subsection, subIdx) => (
														<SidebarItem
															key={subsection.id || subIdx}
															id={subsection.id}
															name={subsection.title}
															activeSection={activeSection}
															handleNavigate={handleNavigate}
															openDropdown={openDropdown}
															toggleDropdown={toggleDropdown}
															isLastItem={subIdx === section.subsections.length - 1}
															onDelete={(id) => console.log('Delete Subsection', id)}
															onRename={(id) => console.log('Rename Subsection', id)}
															onMove={(itemId, direction) => {
																// Move subsection within its parent section
																if (onMoveSubsection) {
																	onMoveSubsection(category.id, section.id, itemId, direction);
																}
															}}
															onPreview={(id) => {
																if (onPreviewSection) onPreviewSection(id);
															}}
															onSampleContent={(id) => {
																if (onSampleContent) onSampleContent(id);
															}}
														/>
													))}
												</div>
											)}
										</div>
									))}

									{/* Add Sections Area - Optional sections that can be added */}
									{category.optionalSections && category.optionalSections.length > 0 && (
										<div className="w-full mt-4 pt-3 border-t border-lightGrey">
											<div className="flex items-center gap-2 mb-2">
												<Plus className="w-3.5 h-3.5 text-purple" />
												<span className="text-sm font-medium text-purple">Add Sections</span>
											</div>
											<div className="flex flex-col gap-1">
												{category.optionalSections.map((optSection, optIdx) => (
													<OptionalSectionItem
														key={optSection.id || optIdx}
														id={optSection.id}
														name={optSection.title}
														onAdd={(sectionId) => {
															if (onAddSection) {
																onAddSection(category.id, sectionId);
															}
														}}
													/>
												))}
											</div>
										</div>
									)}
								</div>
							)}
						</div>
					);
				})
				: // Old structure (fallback)
				sections.map((section, idx) => {
					const isOpen = openSections[section.title];
					const isLastSection = idx === sections.length - 1;

					return (
						<div
							key={idx}
							className={`flex flex-col w-full border-b border-lightGrey px-[25px] ${isOpen ? 'gap-[30px] py-[18px]' : 'py-[18px]'
								}`}
						>
							{/* Section Header */}
							<button
								onClick={() => toggleSection(section.title)}
								className="w-full flex justify-between items-center text-dark-blue font-semibold text-20 text-left"
							>
								<div className="flex items-center gap-2 text-left min-w-0 flex-1">
									<span className="truncate w-full block">{section.title}</span>
								</div>
								<div
									className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'
										}`}
								>
									<ChevronDown className="w-4 h-4" />
								</div>
							</button>

							{/* Expanded Content */}
							{isOpen && (
								<div className="flex flex-col items-start gap-2 w-full max-w-full ">
									{/* Main section item */}
									<SidebarItem
										id={section.id}
										name={section.title}
										activeSection={activeSection}
										handleNavigate={handleNavigate}
										openDropdown={openDropdown}
										toggleDropdown={toggleDropdown}
										isLastItem={isLastSection}
										onDelete={(id) => console.log('Delete Section', id)}
										onRename={(id) => console.log('Rename Section', id)}
										onMove={(itemId, direction) => console.log('Move Section', itemId, direction)}
										onPreview={(id) => {
											if (onPreviewSection) onPreviewSection(id);
										}}
										onSampleContent={(id) => {
											if (onSampleContent) onSampleContent(id);
										}}
									/>

									{/* Subsections or Pages */}
									{(section.subsections || section.pages || []).map((item, pidx, arr) => (
										<SidebarItem
											key={pidx}
											id={item.id}
											name={item.title || item.name}
											activeSection={activeSection}
											handleNavigate={handleNavigate}
											openDropdown={openDropdown}
											toggleDropdown={toggleDropdown}
											isLastItem={pidx === arr.length - 1}
											onDelete={(id) => console.log('Delete Item', id)}
											onRename={(id) => console.log('Rename Item', id)}
											onMove={(itemId, direction) => console.log('Move Item', itemId, direction)}
											onPreview={(id) => {
												if (onPreviewSection) onPreviewSection(id);
											}}
											onSampleContent={(id) => {
												if (onSampleContent) onSampleContent(id);
											}}
										/>
									))}

									{section.allowAdd && (
										<button className="mt-2 w-full text-purple border border-purple rounded px-2 py-1 text-sm flex items-center justify-center gap-1 hover:bg-purple/10">
											<Plus className="w-4 h-4" /> Add new page
										</button>
									)}
								</div>
							)}
						</div>
					);
				})}
		</div>
	);
}
